import { useMemo } from 'react';

import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import duration from 'dayjs/plugin/duration';

import { ApiError, ErrorResponse } from 'api';
import { useCancelPeriodEnrollment } from 'api/hooks/private/periods/CancelPeriodEnrollment';
import { useEnrollStudentInPeriod } from 'api/hooks/private/periods/EnrollStudentInPeriod';
import { useEnrollStudentInPeriodWaitlist } from 'api/hooks/private/periods/EnrollStudentInPeriodWaitlist';
import { useGetSelfScheduledPeriods } from 'api/hooks/private/users/students/GetSelfSchedulePeriods';
import { useGetStudentCourseEnrollments } from 'api/hooks/private/users/students/GetStudentCourseEnrollments';
import {
  StudentPeriodEnrollment,
  useGetStudentPeriodEnrollments,
} from 'api/hooks/private/users/students/GetStudentPeriods';

import useAuthContext from 'hooks/useAuthContext';
import useConfirmDialog from 'hooks/useConfirmDialog';
import useFeedbackDialog from 'hooks/useFeedbackDialog';
import useSelfScheduleContext from 'hooks/useSelfScheduleContext';

import StudentPage from '../components/StudentPage';
import StudentPeriod from '../StudentDashboard/components/StudentPeriod/StudentPeriod';
import PeriodListHeader from './components/PeariodListHeader';
import SelfScheduleMenu from './components/SelfScheduleMenu';
import SelfSchedulePeriod from './components/SelfSchedulePeriod';

dayjs.extend(advancedFormat);
dayjs.extend(duration);

// error codes
// PERIOD_NOT_FOUND
// STUDENT_NOT_FOUND
// COURSE_PERIOD_FULL
// STUDENT_NOT_ENROLLED_IN_COURSE
// COURSE_SSP_MISSING
//
// SSP_VOILATION_MAX_PERIODS_IN_INTERVAL
// SSP_VOILATION_MAX_PERIODS_IN_ADVANCE
// SSP_VOILATION_MISSING_PERMIT
// SSP_VOILATION_MISSING_PERMIT_EXP
// SSP_VOILATION_PERMIT_EXP
// ALREADY_ENROLLED_IN_PERIOD

function SelfSchedule(): JSX.Element {
  // get the student id from the auth context
  const { userId: studentId } = useAuthContext();

  const { showFeedbackDialog } = useFeedbackDialog();
  const { showConfirmDialog } = useConfirmDialog();

  // get menu state from self schedule context
  const {
    selfScheduleParams: { selectedDate, selectedEnrollment },
  } = useSelfScheduleContext();
  const queryClient = useQueryClient();

  // get performance and observation time left to schedule
  const performanceTimeRemaining = dayjs
    .duration(selectedEnrollment?.performance_time_included ?? { minutes: 0 })
    .subtract(dayjs.duration(selectedEnrollment?.performance_time_scheduled ?? { minutes: 0 }));
  const observationTimeRemaining = dayjs
    .duration(selectedEnrollment?.course_completion_criteria?.observation_time_required ?? {})
    .subtract(selectedEnrollment?.observation_time_scheduled ?? {});

  console.log(`Performance Time Remaining: ${performanceTimeRemaining.asMinutes()} minutes`);
  console.log(`Observation Time Remaining: ${observationTimeRemaining.asMinutes()} minutes`);

  // fetch student course enrollments and count how many self scheduled courses the student is enrolled in
  const courseEnrollments = useGetStudentCourseEnrollments({
    variables: {
      id: studentId ?? '',
    },
    enabled: !!studentId,
  });
  const selfScheduleCourseCount = useMemo(
    () =>
      courseEnrollments?.data?.filter((enrollment) => enrollment?.course?.schedule_type === 'self_scheduled')?.length ??
      0,
    [courseEnrollments],
  );

  // fetch student period enrollments
  const periodEnrollments = useGetStudentPeriodEnrollments({
    variables: {
      id: studentId ?? '',
    },
    enabled: !!studentId,
  });

  // create a map of course period id to student period enrollment
  const studentPeriods = useMemo(() => {
    return periodEnrollments?.data?.reduce(
      (map, enrollment) => map.set(enrollment.course_period_id, enrollment),
      new Map<string, StudentPeriodEnrollment>(),
    );
  }, [periodEnrollments]);

  // get all self scheduled periods for the seleceted enrollment
  const selfSchedulePeriods = useGetSelfScheduledPeriods({
    variables: {
      studentId: studentId ?? '',
      courseEnrollmentId: selectedEnrollment?.id ?? '',
      start: selectedDate.toISOString(),
      end: selectedDate.add(24, 'hours').toISOString(),
    },
    enabled: !!selectedEnrollment?.id,
  });

  // get the number of openings for the selected date
  const numOpenings = useMemo(
    () =>
      selfSchedulePeriods?.data?.filter((period) => period?.enrollment_performance_count < period?.capacity).length ??
      0,
    [selfSchedulePeriods?.data],
  );

  // mutations
  const enrollStudentInPeriod = useEnrollStudentInPeriod({
    onSuccess: (data) => {
      if (data) {
        queryClient.invalidateQueries({ queryKey: ['self_scheduled_course_period_count'] });
        queryClient.invalidateQueries({ queryKey: ['self_scheduled_course_periods'] });
        queryClient.invalidateQueries({ queryKey: ['student_course_periods'] });
        showFeedbackDialog({
          icon: 'success',
          title: 'You have been enrolled in the course period.',
        });
      }
    },
    onError(error: AxiosError<ErrorResponse>) {
      // get the response data
      const response = error?.response?.data;

      if (response?.error?.code?.startsWith('SSP_VOILATION')) {
        showFeedbackDialog({
          title: 'Course Policy Error',
          message: response?.error?.message ?? error.message,
        });
      } else {
        showFeedbackDialog({
          title: 'Error',
          message: response?.error?.message ?? error.message,
        });
      }
    },
  });

  const enrollStudentInPeriodWaitlist = useEnrollStudentInPeriodWaitlist({
    onSuccess: (data) => {
      if (data) {
        queryClient.invalidateQueries({ queryKey: ['self_scheduled_course_period_count'] });
        queryClient.invalidateQueries({ queryKey: ['self_scheduled_course_periods'] });
        queryClient.invalidateQueries({ queryKey: ['student_course_periods'] });
        showFeedbackDialog({
          icon: 'success',
          title: 'You have been added to the waitlist.',
        });
      }
    },
    onError(error: AxiosError<ErrorResponse>) {
      // get the response data
      const response = error?.response?.data;

      showFeedbackDialog({
        title: 'Error',
        message: response?.error?.message ?? error.message,
      });
    },
  });

  const cancelPeriodEnrollment = useCancelPeriodEnrollment({
    onSuccess: (data) => {
      if (data) {
        showFeedbackDialog({
          icon: 'success',
          title: 'Course period has been canceled.',
        });
      }
    },
    onError(error: AxiosError<ErrorResponse>) {
      // get the response data
      const response = error?.response?.data;

      showFeedbackDialog({
        title: 'Error',
        message: response?.error?.message ?? error.message,
      });
    },
  });

  const register = async (id: string, enrollmentType: 'performance_and_observation' | 'observation_only') => {
    await enrollStudentInPeriod.mutateAsync({
      coursePeriodId: id,
      studentId: studentId ?? '',
      enrollmentType: enrollmentType,
      overrideRestrictions: false,
    });
  };

  const waitlist = async (id: string, enrollmentType: 'performance_and_observation' | 'observation_only') => {
    // alert(`Waitlist for course period ${id}, ${enrollmentType}`);
    await enrollStudentInPeriodWaitlist.mutateAsync({
      coursePeriodId: id,
      studentId: studentId ?? '',
      enrollmentType: enrollmentType,
    });
  };

  const cancel = async (id: string) => {
    await cancelPeriodEnrollment.mutateAsync({
      coursePeriodId: id,
      studentId: studentId ?? '',
      cancel: true,
    });
  };

  // handle any errors from fetching the self schedule periods
  if (selfSchedulePeriods.isError) {
    // get the error object
    const error = selfSchedulePeriods.error;

    if (error instanceof ApiError) {
      // showFeedbackDialog({
      //   title: 'Error',
      //   message: error.message
      // });
      console.log('instanceof ApiError: ', error);
    }

    // check if the student has already completed the course
    if (error instanceof ApiError && error.code === 'COURSE_ALREADY_COMPLETED') {
      // return (
      //   <StudentPage title="Register For Course Periods" subtitle="You have already completed all self scheduled courses." />
      // );
    }
  }

  // if the student is not enrolled in a self scheduled courses, show a message
  if (courseEnrollments.status === 'success' && selfScheduleCourseCount === 0) {
    return (
      <StudentPage
        title="Register For Course Periods"
        subtitle="You are currently not enrolled in a self scheduled course. Please contact your driving school to set up an appointment."
      />
    );
  }

  return (
    <StudentPage title="Register For Course Periods" menuComponent={<SelfScheduleMenu />}>
      <PeriodListHeader
        courseTitle={selectedEnrollment?.course?.title ?? ''}
        courseSectionTitle={selectedEnrollment?.course_section?.title ?? ''}
        selectedDate={selectedDate}
        numOpenings={numOpenings}
        loading={selfSchedulePeriods.isLoading}
      />

      <Stack direction="column" spacing={2}>
        {!selfSchedulePeriods.isLoading &&
          selfSchedulePeriods?.data?.map((period) => {
            // if the student is already enrolled in the course period
            const studentPeriod = studentPeriods?.get(period.id);
            if (studentPeriod && !studentPeriod?.canceled_at) {
              return (
                <StudentPeriod
                  key={studentPeriod?.course_period?.id}
                  id={studentPeriod?.course_period?.id}
                  start={studentPeriod?.course_period?.start}
                  end={studentPeriod?.course_period?.end}
                  showDate={true}
                  courseTitle={studentPeriod?.course_period?.course_section?.course?.title}
                  sectionTitle={studentPeriod?.course_period?.course_section?.title}
                  attendance={studentPeriod?.attendance}
                  canceledAt={studentPeriod?.canceled_at}
                  allowCancellation={studentPeriod?.course_enrollment?.course_policy?.allow_cancellations ?? false}
                  lateCancellationDeadline={studentPeriod?.course_enrollment?.course_policy?.late_cancellation_deadline}
                  instructors={studentPeriod?.course_period?.instructors?.map(
                    (instructor) => instructor?.formal_name ?? '',
                  )}
                  location={studentPeriod?.course_period?.course_section?.tenant_location?.name}
                  onClickCancel={(id: string) => {
                    showConfirmDialog({
                      title: 'Cancel Period',
                      message: 'Are you sure you want to cancel this period?',
                      danger: true,
                    }).then((confirmed) => {
                      if (confirmed) {
                        cancel(id);
                      }
                    });
                  }}
                />
              );
            }

            return (
              <SelfSchedulePeriod
                key={period?.id}
                id={period?.id}
                start={period?.start}
                end={period?.end}
                openEnrollment={period?.open_enrollment}
                closeEnrollment={period?.close_enrollment}
                capacity={period?.capacity}
                observationCapacity={period?.observation_capacity}
                waitlistCapacity={period?.waitlist_capacity}
                waitlistObservationCapacity={period?.waitlist_observation_capacity}
                enrollmentPerformanceCount={period?.enrollment_performance_count}
                enrollmentObservationCount={period?.enrollment_observation_count}
                waitlistPerformanceCount={period?.waitlist_performance_count}
                waitlistObservationCount={period?.waitlist_observation_count}
                instructors={period?.instructors?.map((instructor) => instructor?.name)}
                location={period?.location}
                showDate={true}
                onClickRegister={register}
                onClickWaitlist={waitlist}
              />
            );
          })}
      </Stack>

      {selfSchedulePeriods.isLoading && (
        <Box display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      )}

      {!selfSchedulePeriods.isLoading &&
        performanceTimeRemaining.asMinutes() === 0 &&
        observationTimeRemaining.asMinutes() === 0 && (
          <Typography align="center" fontWeight={700}>
            You have already scheduled all required drives.
          </Typography>
        )}

      {!selfSchedulePeriods.isLoading && selfSchedulePeriods?.data?.length === 0 && (
        <Typography align="center">
          There are no periods available.
          <br />
          Please select another date.
        </Typography>
      )}

      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={enrollStudentInPeriod?.isPending || enrollStudentInPeriodWaitlist?.isPending}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </StudentPage>
  );
}

export default SelfSchedule;
